import React, { useEffect, useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import {
    Box, Button, CircularProgress, IconButton, Menu, MenuItem, Stack, TextField, Tooltip,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import dayjs from 'dayjs';
import { tokens } from '../../theme';

const dateOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };

const presets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 14 days', days: 14 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 12 months', days: 365 },
];

const fieldSx = {
    width: { xs: 132, sm: 148 },
    '& .MuiOutlinedInput-root': {
        height: 40,
        borderRadius: '10px',
        backgroundColor: tokens.surface,
        fontSize: 13,
        fontWeight: 500,
        color: tokens.ink,
        '& fieldset': { borderColor: tokens.border },
        '&:hover fieldset': { borderColor: tokens.brown },
        '&.Mui-focused fieldset': { borderColor: tokens.accent },
    },
};

const calendarMenuSx = {
    '& .MuiPaper-root': {
        borderRadius: '14px',
        border: `1px solid ${tokens.border}`,
        boxShadow: '0 12px 32px rgba(44, 36, 27, 0.12)',
        overflow: 'hidden',
    },
    '& .MuiDateCalendar-root': {
        width: 300,
        maxHeight: 340,
    },
    '& .MuiPickersDay-root.Mui-selected': {
        backgroundColor: `${tokens.accent} !important`,
    },
    '& .MuiPickersDay-root:hover': {
        backgroundColor: 'rgba(91, 127, 94, 0.12)',
    },
};

export default function DateFilter({ ingredient, dates, style, defaultDays = 7, showLoading }) {
    const [startAnchorElem, setStartAnchorElem] = useState(null);
    const [endAnchorElem, setEndAnchorElem] = useState(null);
    const [presetAnchor, setPresetAnchor] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    useEffect(() => {
        if (startDate === null && endDate === null) {
            const { start, end } = initializeDates();
            setStartDate(start);
            setEndDate(end);
            dates?.(start, end);
        } else if (startDate && endDate) {
            dates?.(startDate, endDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ingredient]);

    const initializeDates = () => {
        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() - defaultDays);
        return {
            start: start.toLocaleDateString('en-US', dateOptions),
            end: today.toLocaleDateString('en-US', dateOptions),
        };
    };

    const applyPreset = (days) => {
        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() - days);
        const startStr = start.toLocaleDateString('en-US', dateOptions);
        const endStr = today.toLocaleDateString('en-US', dateOptions);
        setStartDate(startStr);
        setEndDate(endStr);
        setPresetAnchor(null);
    };

    const pickStart = (value) => {
        if (!value) return;
        setStartDate(value.toDate().toLocaleDateString('en-US', dateOptions));
        setStartAnchorElem(null);
    };

    const pickEnd = (value) => {
        if (!value) return;
        setEndDate(value.toDate().toLocaleDateString('en-US', dateOptions));
        setEndAnchorElem(null);
    };

    const handleSubmit = () => {
        if (startDate && endDate) dates?.(startDate, endDate);
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            className={style ? 'dsh-dt' : 'date-filter'}
            sx={{
                flexWrap: 'wrap',
                width: 'auto',
                maxWidth: '100%',
                p: 0.5,
                borderRadius: '12px',
                backgroundColor: 'rgba(243, 246, 241, 0.75)',
                border: `1px solid ${tokens.border}`,
            }}
        >
            <Button
                size="small"
                endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                onClick={(e) => setPresetAnchor(e.currentTarget)}
                sx={{
                    color: tokens.inkMuted,
                    fontWeight: 600,
                    px: 1.25,
                    minHeight: 40,
                    borderRadius: '10px',
                    '&:hover': { backgroundColor: 'rgba(44,36,27,0.05)', color: tokens.ink },
                }}
            >
                Range
            </Button>

            <TextField
                size="small"
                placeholder="Start"
                value={startDate || ''}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <IconButton size="small" onClick={(e) => setStartAnchorElem(e.currentTarget)} aria-label="Pick start date">
                            <CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: tokens.brown }} />
                        </IconButton>
                    ),
                }}
                sx={fieldSx}
            />

            <Box sx={{ color: tokens.inkMuted, fontSize: 13, px: 0.25 }}>—</Box>

            <TextField
                size="small"
                placeholder="End"
                value={endDate || ''}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <IconButton size="small" onClick={(e) => setEndAnchorElem(e.currentTarget)} aria-label="Pick end date">
                            <CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: tokens.brown }} />
                        </IconButton>
                    ),
                }}
                sx={fieldSx}
            />

            <Tooltip title="Apply date range">
                <span>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={showLoading || !startDate || !endDate}
                        sx={{
                            minWidth: 40,
                            width: 40,
                            height: 40,
                            p: 0,
                            borderRadius: '10px',
                        }}
                    >
                        {showLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ArrowForwardIcon fontSize="small" />}
                    </Button>
                </span>
            </Tooltip>

            <Menu
                open={Boolean(presetAnchor)}
                anchorEl={presetAnchor}
                onClose={() => setPresetAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        border: `1px solid ${tokens.border}`,
                        mt: 0.75,
                        minWidth: 160,
                    },
                }}
            >
                {presets.map((p) => (
                    <MenuItem
                        key={p.days}
                        onClick={() => applyPreset(p.days)}
                        sx={{ fontSize: 14, fontWeight: 500, color: tokens.ink }}
                    >
                        {p.label}
                    </MenuItem>
                ))}
            </Menu>

            <Menu open={Boolean(startAnchorElem)} onClose={() => setStartAnchorElem(null)} anchorEl={startAnchorElem} sx={calendarMenuSx}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                        value={startDate ? dayjs(startDate) : null}
                        onChange={pickStart}
                    />
                </LocalizationProvider>
            </Menu>

            <Menu open={Boolean(endAnchorElem)} onClose={() => setEndAnchorElem(null)} anchorEl={endAnchorElem} sx={calendarMenuSx}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                        value={endDate ? dayjs(endDate) : null}
                        onChange={pickEnd}
                    />
                </LocalizationProvider>
            </Menu>
        </Stack>
    );
}
